import { PartialMessage, PlainMessage } from '@bufbuild/protobuf';
import {
  CreateProductType,
  Reservoir,
  Reservoir_BlueRoof,
  Reservoir_ConventionalRoof,
  Reservoir_CrateType,
  Reservoir_GreenRoof,
  Reservoir_InfiltrationTank,
  Reservoir_Pond,
  Reservoir_Tank,
  Reservoir_Wadi,
} from '@wavingroup/aqora-v2-api/wavin/aqora/v2/system_pb';
import { TFunction } from 'i18next';
import { MetricType } from '@wavingroup/aqora-v2-api/wavin/aqora/v2/metrics_pb';
import { CreateDrainModel } from '~/shared/models/system/CreateDrainModel';
import { CreateProductModel } from '~/shared/models/system/CreateProductModel';
import { IssueModel } from '~/shared/models/issues/IssueModel';
import {
  getRoundedMetricValue,
  roundToNDecimals,
} from '~/shared/models/metrics/metric-conversions';
import { WaterLevelsModel } from '~/shared/models/metrics/waterLevelsModel/WaterLevelsModel';
import { ProductModel } from '~/shared/models/system/ProductModel';
import { ReservoirFormValues } from '~/shared/models/system/ReservoirFormTypes';
import {
  asFloat,
  asInt,
  assertIsDefined,
  assertUnreachable,
} from '~/types/assert-type';
import { ReservoirParameterConfigModel } from '~/shared/models/system/ReservoirParameterConfigModel';
import { ReservoirTranslationsModel } from '~/shared/models/system/ReservoirTranslationsModel';

type ConventionalRoofProperties = PlainMessage<Reservoir_ConventionalRoof>;

type BlueRoofProperties = PlainMessage<Reservoir_BlueRoof>;

type GreenRoofProperties = PlainMessage<Reservoir_GreenRoof>;

type TankProperties = PlainMessage<Reservoir_Tank>;

type InfiltrationTankProperties = PlainMessage<Reservoir_InfiltrationTank>;

type PondProperties = PlainMessage<Reservoir_Pond>;

type WadiProperties = PlainMessage<Reservoir_Wadi>;

type TypeProperties = NonNullable<Reservoir['typeProperties']>;

export type ReservoirType = Exclude<
  TypeProperties extends { case: infer T } ? T : never,
  'sewer' | undefined
>;

export const reservoirCrateTypes = Object.values(Reservoir_CrateType).filter(
  (value) => typeof value === 'number',
);

export type ReservoirParameter =
  | keyof Pick<
      ReservoirFormValues,
      | 'title'
      | 'area'
      | 'areaGreen'
      | 'captureArea'
      | 'crateType'
      | 'factorHollow'
      | 'greenType'
      | 'drainageSpeed'
      | 'dryProtection'
      | 'externalUsage'
      | 'maxWaterHeight'
      | 'minWaterHeight'
      | 'minWaterCapacityPercentage'
      | 'pondNapOffset'
      | 'targetWaterHeight'
      | 'targetWaterCapacityPercentage'
      | 'targetWaterHeightGrowingMonths'
      | 'targetWaterCapacityPercentageGrowingMonths'
      | 'type'
      | 'wadiMaxVolume'
    >
  | 'currentWaterLevel'
  | 'capacityUsedPercentage'
  | 'capacityUsedVolume';

export type EditableReservoirParameter = Exclude<
  ReservoirParameter,
  'currentWaterLevel' | 'capacityUsedPercentage' | 'capacityUsedVolume'
>;

type ReservoirPropertiesMap = {
  conventionalRoof: ConventionalRoofProperties;
  blueRoof: BlueRoofProperties;
  greenRoof: GreenRoofProperties;
  tank: TankProperties;
  infiltrationTank: InfiltrationTankProperties;
  pond: PondProperties;
  wadi: WadiProperties;
};

type ReservoirProperties<T extends ReservoirType> = {
  type: T;
} & ReservoirPropertiesMap[T];

type ReservoirCaseProperties<T extends ReservoirType> = {
  case: T;
  value: ReservoirPropertiesMap[T];
};

type ReservoirFormTypeProperties = {
  [K in ReservoirType]: ReservoirProperties<K>;
}[ReservoirType];

type ReservoirApiTypeProperties = {
  [K in ReservoirType]: ReservoirCaseProperties<K>;
}[ReservoirType];

export type ReservoirParameterDetails = {
  title: string;
  value: string | number;
  unit?: string;
  redirect?: boolean;
  big?: boolean;
  tooltip?: string;
};

export class ReservoirModel {
  readonly title: string;

  readonly id: string;

  readonly name: string;

  readonly maxWaterHeightMm: number;

  private minWaterHeightMm: number;

  private minWaterCapacityPercentage: number = 0;

  private targetWaterHeightMm: number;

  private targetWaterCapacityPercentage: number;

  private targetWaterHeightGrowingMonthsMm: number;

  private targetWaterCapacityPercentageGrowingMonths: number;

  private factorHollow: number;

  readonly products: ProductModel[];

  readonly sideLengthBlue: number;

  readonly areaBlueM2: number = 0;

  readonly type: ReservoirType;

  private typeProperties: ReservoirFormTypeProperties;

  readonly uiPosition: { x: number; y: number };

  readonly issues?: IssueModel[];

  private allowedCreateProductTypes: Set<CreateProductType>;

  private allowPressurePipe: boolean;

  readonly isInAdviceSystemState?: boolean;

  private allowDrain: boolean;

  constructor({
    reservoir,
    products,
    issues,
    isInAdviceSystemState,
  }: {
    reservoir: Reservoir;
    products: ProductModel[];
    issues?: IssueModel[];
    isInAdviceSystemState?: boolean;
  }) {
    this.id = ReservoirModel.extractReservoirId(reservoir.name);
    this.title = reservoir.title;
    this.name = reservoir.name;
    this.maxWaterHeightMm = Number(reservoir.maxWaterHeightMm);

    this.minWaterHeightMm = Number(reservoir.minWaterHeightMm);

    this.minWaterCapacityPercentage = Math.round(
      reservoir.minWaterCapacityPercentage,
    );

    this.targetWaterHeightMm = reservoir.targetWaterHeight;

    this.targetWaterCapacityPercentage = Math.round(
      reservoir.targetCapacityPercentage,
    );

    this.targetWaterHeightGrowingMonthsMm =
      reservoir.targetWaterHeightGrowingMonths;

    this.targetWaterCapacityPercentageGrowingMonths = Math.round(
      reservoir.targetCapacityPercentageGrowingMonths,
    );

    this.factorHollow = Math.round(reservoir.factorHollow * 100);

    this.sideLengthBlue = Math.sqrt(Number(reservoir.areaM2));

    this.areaBlueM2 = Number(reservoir.areaM2);

    this.typeProperties = ReservoirModel.createTypeProperties(
      reservoir.typeProperties,
    );

    this.type = this.typeProperties.type;

    assertIsDefined(reservoir.uiPosition);

    this.uiPosition = reservoir.uiPosition;

    this.products = products;

    this.issues = issues;

    this.allowedCreateProductTypes = new Set(
      reservoir.configOptions?.allowedCreateProductTypes ?? [],
    );

    this.allowPressurePipe =
      reservoir.configOptions?.allowPressurePipe ?? false;

    this.allowDrain = reservoir.configOptions?.allowGravitationalDrain ?? false;

    this.isInAdviceSystemState = isInAdviceSystemState;
  }

  get areaGreenM2() {
    return this.typeProperties.type === 'greenRoof'
      ? this.typeProperties.areaGreen
      : 0;
  }

  static extractReservoirId(name: Reservoir['name']): string {
    const regex = /\/reservoirs\/([^/]+)/;
    const match = name.match(regex);

    assertIsDefined(match);
    return match[1];
  }

  private static createTypeProperties(
    typeProperties: Reservoir['typeProperties'],
  ): ReservoirFormTypeProperties {
    const typeCase = typeProperties.case;
    switch (typeCase) {
      case 'conventionalRoof':
        return {
          type: typeCase,
        };
      case 'blueRoof':
        return {
          type: typeCase,
          crateType: typeProperties.value.crateType,
        };
      case 'greenRoof':
        return {
          type: typeCase,
          areaGreen: typeProperties.value.areaGreen,
          greenType: typeProperties.value.greenType,
          crateType: typeProperties.value.crateType,
        };
      case 'tank':
        return {
          type: typeCase,
          captureArea: typeProperties.value.captureArea,
          dryProtection: typeProperties.value.dryProtection,
          externalUsage: typeProperties.value.externalUsage,
        };
      case 'infiltrationTank':
        return {
          type: typeCase,
          drainageSpeed: roundToNDecimals(
            typeProperties.value.drainageSpeed,
            2,
          ),
        };
      case 'pond':
        return {
          type: typeCase,
          napOffset: typeProperties.value.napOffset,
        };
      case 'wadi':
        return {
          type: typeCase,
          maxVolume: typeProperties.value.maxVolume,
          drainageSpeed: roundToNDecimals(
            typeProperties.value.drainageSpeed,
            2,
          ),
        };
      case 'sewer':
        throw new Error('Reservoir type sewer not expected');
      case undefined:
        throw new Error('Expected a reservoir type, but got undefined');
      default:
        return assertUnreachable(typeCase);
    }
  }

  // API TO FORM VALUES CONVERSION
  asFormValues(): ReservoirFormValues {
    return {
      area: this.areaBlueM2.toString(),
      type: this.typeProperties.type,
      areaGreen:
        'areaGreen' in this.typeProperties
          ? this.typeProperties.areaGreen.toString()
          : '',
      dryProtection:
        'dryProtection' in this.typeProperties
          ? this.typeProperties.dryProtection.toString()
          : '',
      externalUsage:
        'externalUsage' in this.typeProperties
          ? this.typeProperties.externalUsage.toString()
          : '',
      captureArea:
        'captureArea' in this.typeProperties
          ? this.typeProperties.captureArea.toString()
          : '',
      factorHollow: this.factorHollow.toString(),
      greenType:
        'greenType' in this.typeProperties
          ? this.typeProperties.greenType.toString()
          : '',
      drainageSpeed:
        'drainageSpeed' in this.typeProperties
          ? this.typeProperties.drainageSpeed.toString()
          : '',
      maxWaterHeight: this.maxWaterHeightMm.toString(),
      minWaterHeight: this.minWaterHeightMm.toString(),
      minWaterCapacityPercentage: this.minWaterCapacityPercentage.toString(),
      pondNapOffset:
        'napOffset' in this.typeProperties
          ? this.typeProperties.napOffset.toString()
          : '',
      targetWaterHeight: this.targetWaterHeightMm.toString(),
      targetWaterHeightGrowingMonths:
        this.targetWaterHeightGrowingMonthsMm.toString(),
      targetWaterCapacityPercentage:
        this.targetWaterCapacityPercentage.toString(),
      targetWaterCapacityPercentageGrowingMonths:
        this.targetWaterCapacityPercentageGrowingMonths.toString(),
      title: this.title,
      wadiMaxVolume:
        'maxVolume' in this.typeProperties
          ? this.typeProperties.maxVolume.toString()
          : '',
      crateType:
        'crateType' in this.typeProperties
          ? this.typeProperties.crateType.toString()
          : '',
    };
  }

  // FORM VALUES TO API CONVERSION
  private static formValuesToApiTypeProperties(
    formValues: ReservoirFormValues,
  ): ReservoirApiTypeProperties {
    switch (formValues.type) {
      case 'conventionalRoof':
        return {
          case: 'conventionalRoof',
          value: {},
        };
      case 'blueRoof':
        return {
          case: 'blueRoof',
          value: {
            crateType: asInt(formValues.crateType),
          },
        };
      case 'greenRoof':
        return {
          case: 'greenRoof',
          value: {
            areaGreen: asFloat(formValues.areaGreen),
            greenType: asInt(formValues.greenType),
            crateType: asInt(formValues.crateType),
          },
        };
      case 'tank':
        return {
          case: 'tank',
          value: {
            dryProtection: asInt(formValues.dryProtection),
            externalUsage: asInt(formValues.externalUsage),
            captureArea: asFloat(formValues.captureArea),
          },
        };
      case 'infiltrationTank':
        return {
          case: 'infiltrationTank',
          value: {
            drainageSpeed: asFloat(formValues.drainageSpeed),
          },
        };
      case 'pond':
        return {
          case: 'pond',
          value: {
            napOffset: asFloat(formValues.pondNapOffset),
          },
        };
      case 'wadi':
        return {
          case: 'wadi',
          value: {
            maxVolume: asFloat(formValues.wadiMaxVolume),
            drainageSpeed: asFloat(formValues.drainageSpeed),
          },
        };
      default:
        return assertUnreachable(formValues.type);
    }
  }

  private static formValueToApiReservoirParameter(
    reservoirParameter: ReservoirParameter,
    formValues: ReservoirFormValues,
  ): Partial<PlainMessage<Reservoir>> | null {
    switch (reservoirParameter) {
      case 'title':
        return { title: formValues.title };
      case 'area':
        return { areaM2: BigInt(formValues.area) };
      case 'factorHollow':
        return { factorHollow: asInt(formValues.factorHollow) / 100 };
      case 'maxWaterHeight':
        return { maxWaterHeightMm: BigInt(formValues.maxWaterHeight) };
      case 'minWaterHeight':
        return { minWaterHeightMm: BigInt(formValues.minWaterHeight) };
      case 'targetWaterHeight':
        return { targetWaterHeight: asFloat(formValues.targetWaterHeight) };
      case 'targetWaterHeightGrowingMonths':
        return {
          targetWaterHeightGrowingMonths: asFloat(
            formValues.targetWaterHeightGrowingMonths,
          ),
        };
      case 'minWaterCapacityPercentage':
        return {
          minWaterCapacityPercentage: asInt(
            formValues.minWaterCapacityPercentage,
          ),
        };
      case 'targetWaterCapacityPercentage':
        return {
          targetCapacityPercentage: asInt(
            formValues.targetWaterCapacityPercentage,
          ),
        };
      case 'targetWaterCapacityPercentageGrowingMonths':
        return {
          targetCapacityPercentageGrowingMonths: asInt(
            formValues.targetWaterCapacityPercentageGrowingMonths,
          ),
        };
      case 'wadiMaxVolume':
      case 'areaGreen':
      case 'pondNapOffset':
      case 'crateType':
      case 'greenType':
      case 'dryProtection':
      case 'externalUsage':
      case 'captureArea':
      case 'drainageSpeed':
      case 'type':
      case 'currentWaterLevel':
      case 'capacityUsedPercentage':
      case 'capacityUsedVolume':
        return null;
      default:
        return assertUnreachable(reservoirParameter);
    }
  }

  toApiReservoir(formValues: ReservoirFormValues): Reservoir {
    const reservoirAttributes: PartialMessage<Reservoir> = {
      name: this.name,
      uiPosition: this.uiPosition,
      typeProperties: ReservoirModel.formValuesToApiTypeProperties(formValues),
    };

    const editable = ReservoirParameterConfigModel.getEditableFields({
      reservoirType: this.type,
      inAdviceSystemState: this.isInAdviceSystemState,
    });

    for (const param of editable) {
      const fieldObj = ReservoirModel.formValueToApiReservoirParameter(
        param,
        formValues,
      );
      if (fieldObj) {
        Object.assign(reservoirAttributes, fieldObj);
      }
    }

    return new Reservoir(reservoirAttributes);
  }

  // SHOWABLE PARAMETERS
  static getUnitForParameter(parameter: ReservoirParameter, t: TFunction) {
    switch (parameter) {
      case 'currentWaterLevel':
      case 'minWaterHeight':
      case 'maxWaterHeight':
      case 'targetWaterHeight':
      case 'targetWaterHeightGrowingMonths':
        return 'mm';
      case 'area':
      case 'areaGreen':
      case 'captureArea':
        return 'm²';
      case 'minWaterCapacityPercentage':
      case 'targetWaterCapacityPercentage':
      case 'targetWaterCapacityPercentageGrowingMonths':
      case 'capacityUsedPercentage':
      case 'factorHollow':
        return '%';
      case 'drainageSpeed':
        return t('reservoirEdit.fields.units.drainageSpeed');
      case 'externalUsage':
        return t('reservoirEdit.fields.units.externalUsage');
      case 'capacityUsedVolume':
        return 'm³';
      case 'wadiMaxVolume':
        return 'l';
      case 'pondNapOffset':
        return 'm';
      default:
        return '';
    }
  }

  private getInfoForParameter(
    parameter: ReservoirParameter,
    t: TFunction,
    waterLevels?: WaterLevelsModel,
    showPredictions?: boolean,
  ): ReservoirParameterDetails {
    switch (parameter) {
      case 'area':
        return {
          title: t('reservoirEdit.fields.labels.area'),
          value: this.areaBlueM2,
          unit: ReservoirModel.getUnitForParameter('area', t),
          tooltip: t('reservoirEdit.fields.tooltips.area'),
        };

      case 'minWaterHeight':
        return {
          title: t('reservoirEdit.fields.labels.minWaterHeight'),
          value: this.minWaterHeightMm,
          unit: ReservoirModel.getUnitForParameter('minWaterHeight', t),
          tooltip: t('reservoirEdit.fields.tooltips.minWaterHeight'),
        };

      case 'minWaterCapacityPercentage':
        return {
          title: t('reservoirEdit.fields.labels.minWaterCapacityPercentage'),
          value: this.minWaterCapacityPercentage,
          unit: ReservoirModel.getUnitForParameter(
            'minWaterCapacityPercentage',
            t,
          ),
          tooltip: t(
            'reservoirEdit.fields.tooltips.minWaterCapacityPercentage',
          ),
        };

      case 'maxWaterHeight':
        return {
          title: t('reservoirEdit.fields.labels.maxWaterHeight'),
          value: this.maxWaterHeightMm,
          unit: ReservoirModel.getUnitForParameter('maxWaterHeight', t),
          tooltip: t('reservoirEdit.fields.tooltips.maxWaterHeight'),
        };

      case 'targetWaterHeight':
        return {
          title: t('reservoirEdit.fields.labels.targetWaterHeight'),
          value: this.targetWaterHeightMm,
          unit: ReservoirModel.getUnitForParameter('targetWaterHeight', t),
          tooltip: t('reservoirEdit.fields.tooltips.targetWaterHeight'),
        };

      case 'targetWaterHeightGrowingMonths':
        return {
          title: t(
            'reservoirEdit.fields.labels.targetWaterHeightGrowingMonths',
          ),
          value: this.targetWaterHeightGrowingMonthsMm,
          unit: ReservoirModel.getUnitForParameter(
            'targetWaterHeightGrowingMonths',
            t,
          ),
          tooltip: t(
            'reservoirEdit.fields.tooltips.targetWaterHeightGrowingMonths',
          ),
        };

      case 'targetWaterCapacityPercentage':
        return {
          title: t('reservoirEdit.fields.labels.targetWaterCapacityPercentage'),
          value: this.targetWaterCapacityPercentage,
          unit: ReservoirModel.getUnitForParameter(
            'targetWaterCapacityPercentage',
            t,
          ),
          tooltip: t(
            'reservoirEdit.fields.tooltips.targetWaterCapacityPercentage',
          ),
        };

      case 'targetWaterCapacityPercentageGrowingMonths':
        return {
          title: t(
            'reservoirEdit.fields.labels.targetWaterCapacityPercentageGrowingMonths',
          ),
          value: this.targetWaterCapacityPercentageGrowingMonths,
          unit: ReservoirModel.getUnitForParameter(
            'targetWaterCapacityPercentageGrowingMonths',
            t,
          ),
          tooltip: t(
            'reservoirEdit.fields.tooltips.targetWaterCapacityPercentageGrowingMonths',
          ),
        };

      case 'factorHollow':
        return {
          title: t('reservoirEdit.fields.labels.factorHollow'),
          value: this.factorHollow,
          unit: ReservoirModel.getUnitForParameter('factorHollow', t),
          tooltip: t('reservoirEdit.fields.tooltips.factorHollow'),
        };

      case 'title':
        return {
          title: t('reservoirEdit.fields.labels.reservoirTitle'),
          value: this.title,
        };

      case 'crateType':
        return {
          title: t('reservoirEdit.fields.labels.crateType'),
          value:
            'crateType' in this.typeProperties
              ? ReservoirTranslationsModel.getLabelForCrateType(
                  this.typeProperties.crateType,
                  t,
                )
              : '',
        };

      case 'greenType':
        return {
          title: t('reservoirEdit.fields.labels.greenType'),
          value:
            'greenType' in this.typeProperties
              ? ReservoirTranslationsModel.getLabelForGreenType(
                  this.typeProperties.greenType,
                  t,
                )
              : '',
        };

      case 'areaGreen':
        return {
          title: t('reservoirEdit.fields.labels.areaGreen'),
          value:
            'areaGreen' in this.typeProperties
              ? this.typeProperties.areaGreen
              : '',
          unit: ReservoirModel.getUnitForParameter('areaGreen', t),
          tooltip: t('reservoirEdit.fields.tooltips.areaGreen'),
        };

      case 'dryProtection':
        return {
          title: t('reservoirEdit.fields.labels.dryProtection'),
          value:
            'dryProtection' in this.typeProperties
              ? ReservoirTranslationsModel.getLabelForDryProtection(
                  this.typeProperties.dryProtection,
                  t,
                )
              : '',
          tooltip: t('reservoirEdit.fields.tooltips.dryProtection'),
        };

      case 'externalUsage':
        return {
          title: t('reservoirEdit.fields.labels.externalUsage'),
          value:
            'externalUsage' in this.typeProperties
              ? this.typeProperties.externalUsage
              : '',
          unit: ReservoirModel.getUnitForParameter('externalUsage', t),
          tooltip: t('reservoirEdit.fields.tooltips.externalUsage'),
        };

      case 'captureArea':
        return {
          title: t('reservoirEdit.fields.labels.captureArea'),
          value:
            'captureArea' in this.typeProperties
              ? this.typeProperties.captureArea
              : '',
          unit: ReservoirModel.getUnitForParameter('captureArea', t),
          tooltip: t('reservoirEdit.fields.tooltips.captureArea'),
        };

      case 'drainageSpeed':
        return {
          title: t('reservoirEdit.fields.labels.drainageSpeed'),
          value:
            'drainageSpeed' in this.typeProperties
              ? this.typeProperties.drainageSpeed
              : '',
          unit: ReservoirModel.getUnitForParameter('drainageSpeed', t),
          tooltip: t('reservoirEdit.fields.tooltips.drainageSpeed'),
        };

      case 'pondNapOffset':
        return {
          title: t('reservoirEdit.fields.labels.pondNapOffset'),
          value:
            'napOffset' in this.typeProperties
              ? this.typeProperties.napOffset
              : '',
          unit: ReservoirModel.getUnitForParameter('pondNapOffset', t),
        };

      case 'wadiMaxVolume':
        return {
          title: t('reservoirEdit.fields.labels.wadiMaxVolume'),
          value:
            'maxVolume' in this.typeProperties
              ? this.typeProperties.maxVolume
              : '',
          unit: ReservoirModel.getUnitForParameter('wadiMaxVolume', t),
          tooltip: t('reservoirEdit.fields.tooltips.wadiMaxVolume'),
        };

      case 'currentWaterLevel':
        return {
          title: t('reservoirEdit.fields.labels.currentWaterLevel'),
          value: waterLevels
            ? getRoundedMetricValue({
                metricType: MetricType.WATER_HEIGHT_MM,
                value: waterLevels.getWaterLevelByReservoirName(this.name) || 0,
              }) || 0
            : 0,
          unit: ReservoirModel.getUnitForParameter('currentWaterLevel', t),
          redirect: !!showPredictions,
          big: true,
        };

      case 'capacityUsedPercentage':
        return {
          title: t('reservoirEdit.fields.labels.capacityUsedPercentage'),
          value: waterLevels
            ? getRoundedMetricValue({
                metricType: MetricType.CALCULATED_WATER_PERCENTAGE,
                value: waterLevels.getCapacityUsedPercentage(this.name) || 0,
              }) || 0
            : 0,
          unit: ReservoirModel.getUnitForParameter('capacityUsedPercentage', t),
          redirect: !!showPredictions,
          big: true,
        };

      case 'capacityUsedVolume':
        return {
          title: t('reservoirEdit.fields.labels.capacityUsedVolume'),
          value: waterLevels
            ? getRoundedMetricValue({
                metricType: MetricType.CALCULATED_WATER_VOLUME_M3,
                value: waterLevels.getCapacityUsedVolume(this.name) || 0,
              }) || 0
            : 0,
          unit: ReservoirModel.getUnitForParameter('capacityUsedVolume', t),
          redirect: !!showPredictions,
          big: true,
        };

      case 'type':
        return {
          title: t('reservoirEdit.fields.labels.type'),
          value: ReservoirTranslationsModel.getLabelForType(this.type, t),
        };

      default:
        return assertUnreachable(parameter);
    }
  }

  public getReservoirParameterDetails({
    t,
    waterLevels,
    showAdditionalData = true,
    showPredictions,
  }: {
    t: TFunction;
    waterLevels?: WaterLevelsModel;
    showAdditionalData?: boolean;
    showPredictions?: boolean;
  }): ReservoirParameterDetails[] {
    const parameters = ReservoirParameterConfigModel.getDisplayableParameters({
      reservoirType: this.type,
      full: showAdditionalData,
    });

    return parameters.map((param) =>
      this.getInfoForParameter(param, t, waterLevels, showPredictions),
    );
  }

  // PRODUCT AND DRAIN CREATION
  public getProductCreationDetails(t: TFunction) {
    const productTypes = [
      CreateProductType.TAP,
      CreateProductType.RAIN_METER,
      CreateProductType.STAAL_RADAR_LEVEL,
      CreateProductType.LEVEL_SENSOR,
    ] as const;

    return productTypes
      .filter((type) => this.allowedCreateProductTypes.has(type))
      .map((type) =>
        CreateProductModel.getCreateProductDetails(
          {
            type,
            reservoirName: this.name,
          },
          t,
        ),
      );
  }

  public getDrainCreationDetails(t: TFunction) {
    return [
      { enabled: this.allowDrain, pressurePipe: false },
      { enabled: this.allowPressurePipe, pressurePipe: true },
    ]
      .filter(({ enabled }) => enabled)
      .map(({ pressurePipe }) =>
        CreateDrainModel.getCreateDrainDetails(
          {
            pressurePipe,
            fromReservoirName: this.name,
          },
          t,
        ),
      );
  }
}
